import { useState, useEffect, useCallback } from 'react'

import { SecureStorage } from '@aparajita/capacitor-secure-storage'

import { MESSAGES } from '@/constants/messages'
import { Logger } from '@/lib/logger'

import {
  CHARGING_FINISHED_STATUSES,
  ChargingSessionInAppNotificationPayload,
} from '@/lib/in-app-notifications/categories/charging_session'
import { InAppNotificationData } from '@/lib/in-app-notifications/types'

const logger = new Logger('useActiveChargingSession')

const ACTIVE_SESSION_STORAGE_KEY = '@mred:active_session'

export function useActiveChargingSession() {
  const [activeSession, setActiveSession] =
    useState<InAppNotificationData<ChargingSessionInAppNotificationPayload> | null>(null)

  useEffect(() => {
    loadPersistedSession()
  }, [])

  useEffect(() => {
    progamaticallyClearActiveSession()
  }, [activeSession])

  const progamaticallyClearActiveSession = useCallback(() => {
    if (!activeSession) return

    const { start_time, status, charge_finished_at } = activeSession.data.payload

    const isFinished = CHARGING_FINISHED_STATUSES.includes(status)

    if (isFinished && charge_finished_at && start_time) {
        const startTime = new Date(start_time).getTime()
        const now = new Date().getTime()
        const diffHours = (now - startTime) / (1000 * 60 * 60)

        if (diffHours > 24) {
          clearActiveSession()
          return
        }

        const finishedTime = new Date(charge_finished_at).getTime()
        const diffFinishedHours = (now - finishedTime) / (1000 * 60 * 60)
        if (diffFinishedHours > 2) {
          clearActiveSession()
        }
    }
  }, [activeSession])

  const loadPersistedSession = async () => {
    try {
      const storedSession = await SecureStorage.get(ACTIVE_SESSION_STORAGE_KEY)
      if (!storedSession) return

      const parsedSession = JSON.parse(storedSession as string)
      setActiveSession(parsedSession as InAppNotificationData<ChargingSessionInAppNotificationPayload>)
    } catch (e) {
      logger.error(MESSAGES.CHARGING_SESSION.ERROR_LOADING_SESSION, e)
    }
  }

  const updatePersistedSession = useCallback(
    async (newSession: InAppNotificationData<ChargingSessionInAppNotificationPayload>) => {
      try {
        const isNewSession =
          !activeSession || activeSession.data.payload.session_id !== newSession.data.payload.session_id
        const isNewStatus = !activeSession || activeSession.data.payload.status !== newSession.data.payload.status
        const isNewCost = !activeSession || activeSession.data.payload.total_cost !== newSession.data.payload.total_cost

        if (isNewSession || isNewStatus || isNewCost) {
          await SecureStorage.set(ACTIVE_SESSION_STORAGE_KEY, JSON.stringify(newSession))
        }
      } catch (e) {
        logger.error(MESSAGES.CHARGING_SESSION.ERROR_UPDATING_STORAGE, e)
      }
    },
    [activeSession]
  )

  const updateActiveSession = useCallback(
    async (newSession: InAppNotificationData<ChargingSessionInAppNotificationPayload>) => {
      setActiveSession(newSession)
      updatePersistedSession(newSession)
    },
    [activeSession]
  )

  const clearPersistedSession = useCallback(async () => {
    try {
      await SecureStorage.remove(ACTIVE_SESSION_STORAGE_KEY)
    } catch (e) {
      logger.error(MESSAGES.CHARGING_SESSION.ERROR_CLEARING_STORAGE, e)
    }
  }, [])

  const clearActiveSession = useCallback(async () => {
    setActiveSession(null)
    clearPersistedSession()
  }, [])

  return { activeSession, updateActiveSession, clearActiveSession }
}
